import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';
import { startTrainingSession, submitTrainingAnswer, completeTrainingSession } from '../services/api';
import useSpeech from '../hooks/useSpeech';
import { evaluateTrainerAnswer } from '../services/audioEvalApi';

const AITrainerSessionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const trainingData = location.state;

  // Store effective training data (from navigation or localStorage)
  const [effectiveTrainingData, setEffectiveTrainingData] = useState(null);

  // Core session state
  const [sessionId, setSessionId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [sessionSummary, setSessionSummary] = useState(null);
  const [redirectCountdown, setRedirectCountdown] = useState(0);

  // Chirp 3 HD TTS + audio recording (Gemini 2.5 Flash evaluates per-question)
  const {
    isSpeaking, isListening, isProcessing,
    speak, stopSpeaking,
    startListeningWindow: startSpeechListeningWindow,
    finishListeningWindow,
    cleanup: cleanupSpeech,
  } = useSpeech();
  const [showingIntro, setShowingIntro] = useState(true);
  const [trainingStarted, setTrainingStarted] = useState(false);
  const [answerPhase, setAnswerPhase] = useState('idle');
  const [listeningRemaining, setListeningRemaining] = useState(0);
  const [lastEvaluation, setLastEvaluation] = useState(null);
  const [questionTimer, setQuestionTimer] = useState(0); // countdown per question
  const lastAudioBlobRef = useRef(null);
  const questionTimerRef = useRef(null);

  // Refs to avoid stale closures in TTS callbacks
  const trainingStartedRef = useRef(false);
  const currentQuestionIndexRef = useRef(-1);

  // Video refs
  const videoRef = useRef(null);
  const cameraStreamRef = useRef(null);

  // Face detection and proctoring
  const [faceDetectionStatus, setFaceDetectionStatus] = useState('checking');
  const [faceCount, setFaceCount] = useState(0);
  const [warningBanner, setWarningBanner] = useState({ show: false, message: '' });
  const [lastWarningTime, setLastWarningTime] = useState(0);
  const [gracePeriodActive, setGracePeriodActive] = useState(true);
  const proctoringIntervalRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const [warnings, setWarnings] = useState([]);

  // ─── Theme colors matching AssessmentInterviewPage ───
  const colors = {
    primary: '#0d9488',
    primaryHover: '#0f766e',
    primaryLight: '#ccfbf1',
    primaryText: '#134e4a',
    bg: '#f6f6f8',
    bgDark: '#101622',
    card: '#ffffff',
    cardDark: '#1a2230',
    border: '#e7ebf3',
    borderDark: '#2a374f',
    text: '#0d121b',
    textSecondary: '#4c669a',
    textDark: '#f8f9fc',
    textSecondaryDark: '#9fb3d9',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
  };

  // Re-attach camera stream when view changes (intro ↔ main have different video elements)
  useEffect(() => {
    if (cameraStreamRef.current && videoRef.current) {
      videoRef.current.srcObject = cameraStreamRef.current;
      videoRef.current.play().catch(() => {});
      console.log('Camera stream re-attached to video element');
    }
  }, [showingIntro, trainingStarted]);

  useEffect(() => {
    console.log('AITrainerSessionPage - Training data received:', trainingData);

    let finalTrainingData = trainingData;

    if (!trainingData) {
      console.log('No training data from navigation, checking localStorage...');
      try {
        const storedData = localStorage.getItem('ai_trainer_data');
        if (storedData) {
          finalTrainingData = JSON.parse(storedData);
          console.log('Retrieved training data from localStorage:', finalTrainingData);
        }
      } catch (error) {
        console.error('Error parsing stored training data:', error);
      }
    }

    if (!finalTrainingData) {
      console.error('No training data found, redirecting to AI trainer setup');
      setTimeout(() => { navigate('/ai-trainer'); }, 1000);
      return;
    }

    if (!finalTrainingData.candidateEmail || !finalTrainingData.candidateName) {
      console.error('Invalid training data, missing required fields:', finalTrainingData);
      alert('Training session data is incomplete. Please start again.');
      navigate('/ai-trainer');
      return;
    }

    console.log('Training data validated, initializing session...');
    setEffectiveTrainingData(finalTrainingData);
    initializeSessionWithData(finalTrainingData);

    const handleBeforeUnload = () => {
      cleanupSpeech();
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach(track => track.stop());
      }
      stopProctoring();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleBeforeUnload);

    return () => {
      console.log('Cleaning up AI trainer session...');
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
      stopProctoring();
      cleanupSpeech();
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach(track => track.stop());
        cameraStreamRef.current = null;
      }
      if (videoRef.current) {
        if (videoRef.current.srcObject) videoRef.current.srcObject = null;
        videoRef.current.pause();
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
        warningTimeoutRef.current = null;
      }
      if (questionTimerRef.current) {
        clearInterval(questionTimerRef.current);
        questionTimerRef.current = null;
      }
    };
  }, [trainingData, navigate]);

  // ─── Session Init ───
  const initializeSessionWithData = async (dataToUse) => {
    try {
      setLoading(true);
      const currentSession = SessionManager.getCurrentSession();
      const candidateId = currentSession?.id || currentSession?.user_id;
      console.log('Linking training session to candidate ID:', candidateId);

      const response = await startTrainingSession({
        candidate_id: candidateId,
        candidate_email: dataToUse.candidateEmail,
        candidate_name: dataToUse.candidateName,
        session_type: dataToUse.sessionType,
        job_title: dataToUse.jobTitle,
        job_description: dataToUse.jobDescription,
        resume_text: dataToUse.resumeText,
        difficulty_level: dataToUse.difficultyLevel,
        num_questions: dataToUse.numQuestions
      });

      setSessionId(response.session_id);
      setQuestions(response.questions);

      await startCamera();

      setTimeout(() => {
        const welcomeMessage = `Hello ${dataToUse.candidateName}, welcome to your AI interview training session. This session will consist of ${response.questions.length} practice questions. I will ask each question, and you can respond using your voice. After each answer, you'll receive detailed feedback. When you're ready, click start training.`;
        speakMessage(welcomeMessage);
      }, 1000);
    } catch (error) {
      console.error('Error starting training session:', error);
      alert('Failed to start training session. Please try again.');
      navigate('/ai-trainer');
    } finally {
      setLoading(false);
    }
  };

  // ─── Camera ───
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480 },
        audio: true
      });
      cameraStreamRef.current = stream;
      stream.getAudioTracks().forEach(track => { track.enabled = true; });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Error starting camera:', err);
      alert('Camera access denied. Please allow camera and microphone access.');
    }
  };

  // ─── TTS ─── (use refs to avoid stale closure)
  // Track the current speech ID to ignore stale onEnd callbacks
  const speechIdRef = useRef(0);

  const speakMessage = (text) => {
    const mySpeechId = ++speechIdRef.current;
    speak(text, {
      onEnd: () => {
        if (mySpeechId !== speechIdRef.current) return; // stale callback, ignore
        console.log('TTS ended. trainingStartedRef:', trainingStartedRef.current, 'questionIndexRef:', currentQuestionIndexRef.current);
        if (currentQuestionIndexRef.current >= 0 && trainingStartedRef.current) {
          startThinkingThenListen();
        }
      },
      onError: () => {
        if (mySpeechId !== speechIdRef.current) return; // stale callback, ignore
        console.log('TTS error. trainingStartedRef:', trainingStartedRef.current, 'questionIndexRef:', currentQuestionIndexRef.current);
        if (currentQuestionIndexRef.current >= 0 && trainingStartedRef.current) {
          startThinkingThenListen();
        }
      },
    });
  };

  // ─── Question Timer (60s per question) ───
  const questionTimerValueRef = useRef(60);

  const startQuestionTimer = () => {
    questionTimerValueRef.current = 60;
    setQuestionTimer(60);
    if (questionTimerRef.current) clearInterval(questionTimerRef.current);
    questionTimerRef.current = setInterval(() => {
      questionTimerValueRef.current -= 1;
      const newVal = questionTimerValueRef.current;
      setQuestionTimer(newVal);
      if (newVal <= 0) {
        clearInterval(questionTimerRef.current);
        questionTimerRef.current = null;
        // Auto-submit when timer hits 0 — called OUTSIDE setState to ensure it fires reliably
        finishListeningWindow();
      }
    }, 1000);
  };

  const stopQuestionTimer = () => {
    if (questionTimerRef.current) {
      clearInterval(questionTimerRef.current);
      questionTimerRef.current = null;
    }
    questionTimerValueRef.current = 0;
    setQuestionTimer(0);
  };

  // ─── Think → Listen ───
  const startThinkingThenListen = async () => {
    setAnswerPhase('thinking');
    for (let i = 5; i > 0; i--) {
      await new Promise(r => setTimeout(r, 1000));
    }
    startListeningWindow();
  };

  const startListeningWindow = () => {
    if (!cameraStreamRef.current) return;
    setAnswerPhase('listening');
    startQuestionTimer();

    startSpeechListeningWindow(cameraStreamRef.current, {
      minSeconds: 10,
      maxSeconds: 60,
      onTick: ({ remaining }) => setListeningRemaining(remaining),
      onComplete: async (audioBlob) => {
        lastAudioBlobRef.current = audioBlob;
        setAnswerPhase('evaluating');

        if (audioBlob && sessionId) {
          try {
            const qIdx = currentQuestionIndexRef.current;
            const currentQ = questions[qIdx];
            const result = await evaluateTrainerAnswer(audioBlob, sessionId, currentQ.question_number);
            setLastEvaluation(result.evaluation);
            setAnswerPhase('feedback');

            const spokenFeedback = result.spoken_feedback || `You scored ${result.evaluation?.score || 0}%.`;
            speakFeedbackOnly(spokenFeedback);
          } catch (err) {
            console.error('Gemini eval error:', err);
            try {
              await submitTrainingAnswer({
                session_id: sessionId,
                question_number: questions[currentQuestionIndexRef.current].question_number,
                answer_text: '(Audio evaluation failed)',
              });
            } catch (e) { /* ignore */ }
            setLastEvaluation({ score: 0, feedback: 'Audio evaluation failed. Click Next Question to continue.' });
            setAnswerPhase('feedback');
          }
        } else {
          setLastEvaluation({ score: 0, feedback: 'No audio recorded. Click Next Question to continue.' });
          setAnswerPhase('feedback');
        }
      },
    });
  };

  const speakFeedbackOnly = (feedbackMessage) => {
    speak(feedbackMessage, {
      onEnd: () => { /* stay on feedback phase — user clicks Next Question */ },
      onError: () => { /* stay on feedback phase — user clicks Next Question */ },
    });
  };

  const moveToNextQuestion = () => {
    setAnswerPhase('idle');
    setLastEvaluation(null);
    lastAudioBlobRef.current = null;
    // Cancel any ongoing speech (feedback, evaluation, etc.) before speaking next question
    stopSpeaking();
    speechIdRef.current++; // invalidate any pending onEnd callbacks
    stopQuestionTimer();

    if (currentQuestionIndexRef.current < questions.length - 1) {
      const nextIdx = currentQuestionIndexRef.current + 1;
      setCurrentQuestionIndex(nextIdx);
      currentQuestionIndexRef.current = nextIdx;
      setTimeout(() => {
        const nextQuestion = questions[nextIdx];
        // Speak ONLY the question text — no evaluation or previous context
        speakMessage(`Question ${nextQuestion.question_number}: ${nextQuestion.question_text}`);
      }, 1000);
    } else {
      handleCompleteSession();
    }
  };

  const stopListening = () => { finishListeningWindow(); };

  // ─── Submit current answer early ───
  const submitAnswer = () => {
    stopQuestionTimer();
    if (isListening || answerPhase === 'listening') {
      finishListeningWindow();
    }
  };

  // ─── Proctoring ───
  const issueWarning = (message) => {
    const now = Date.now();
    if (now - lastWarningTime < 10000) return;
    setLastWarningTime(now);
    setWarningBanner({ show: true, message });
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    warningTimeoutRef.current = setTimeout(() => {
      setWarningBanner({ show: false, message: '' });
    }, 8000);
  };

  const analyzeFrame = async () => {
    if (!videoRef.current || !trainingStartedRef.current || !sessionId) return;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      if (canvas.width === 0 || canvas.height === 0) return;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0);
      const frameData = canvas.toDataURL('image/jpeg', 0.8);

      const response = await fetch('https://aptirix-version-610696728606.asia-south1.run.app/api/interview/analyze-frame', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          interview_id: sessionId,
          question_number: currentQuestionIndex + 1,
          frame_data: frameData
        })
      });

      if (response.ok) {
        const data = await response.json();
        const analysis = data.analysis;
        if (!analysis) return;
        setFaceCount(analysis.face_count || 0);

        if (!gracePeriodActive && analysis.warnings?.length > 0) {
          const warning = analysis.warnings[0];
          const messages = {
            no_face: 'No face detected. Please show your face.',
            multiple_faces: 'Multiple faces detected. Only you should be visible.',
            flat_surface: 'Face covered or object detected.',
            too_dark: 'Video too dark. Please ensure good lighting.',
            too_bright: 'Video too bright. Please adjust lighting.',
          };
          const msg = messages[warning] || 'Face detection issue.';
          setFaceDetectionStatus(warning === 'no_face' || warning === 'flat_surface' ? 'no_face' : warning === 'multiple_faces' ? 'multiple_faces' : faceDetectionStatus);
          issueWarning(msg);
        } else if (analysis.face_count === 1) {
          setFaceDetectionStatus('ok');
        }
        if (!gracePeriodActive && analysis.device_detected) {
          issueWarning('Phone or device detected. Please remove it.');
        }
      }
    } catch (err) { /* silent */ }
  };

  const startProctoring = () => {
    setGracePeriodActive(true);
    setTimeout(() => setGracePeriodActive(false), 10000);
    proctoringIntervalRef.current = setInterval(() => analyzeFrame(), 3000);
  };

  const stopProctoring = () => {
    if (proctoringIntervalRef.current) {
      clearInterval(proctoringIntervalRef.current);
      proctoringIntervalRef.current = null;
    }
  };

  // ─── Actions ───
  const startFirstQuestion = () => {
    setShowingIntro(false);
    setTrainingStarted(true);
    trainingStartedRef.current = true;
    setCurrentQuestionIndex(0);
    currentQuestionIndexRef.current = 0;
    setAnswerPhase('idle');
    if (sessionId) startProctoring();
    setTimeout(() => {
      if (questions.length > 0) {
        speakMessage(`Question ${questions[0].question_number}: ${questions[0].question_text}`);
      }
    }, 500);
  };

  const skipQuestion = async () => {
    stopListening();
    stopSpeaking();
    speechIdRef.current++; // invalidate any pending onEnd callbacks
    stopQuestionTimer();
    try {
      const currentQuestion = questions[currentQuestionIndexRef.current];
      await submitTrainingAnswer({
        session_id: sessionId,
        question_number: currentQuestion.question_number,
        answer_text: '(Skipped)',
      });
    } catch (error) { console.error('Error saving skipped answer:', error); }

    lastAudioBlobRef.current = null;
    setLastEvaluation(null);

    if (currentQuestionIndexRef.current < questions.length - 1) {
      const nextIdx = currentQuestionIndexRef.current + 1;
      setCurrentQuestionIndex(nextIdx);
      currentQuestionIndexRef.current = nextIdx;
      setAnswerPhase('idle');
      setTimeout(() => {
        const nextQuestion = questions[nextIdx];
        speakMessage(`Question ${nextQuestion.question_number}: ${nextQuestion.question_text}`);
      }, 500);
    } else {
      handleCompleteSession();
    }
  };

  const handleCompleteSession = async () => {
    try {
      setLoading(true);
      stopListening();
      stopSpeaking();
      stopProctoring();

      const response = await completeTrainingSession({
        session_id: sessionId,
        send_report: true
      });

      setSessionSummary(response.session_summary);
      setSessionComplete(true);
      setRedirectCountdown(5);

      setTimeout(() => {
        speakMessage(`Congratulations! You've completed your training session with an overall score of ${Math.round(response.session_summary.overall_score)}%. A detailed report has been sent to your email. Great job!`);
      }, 1000);

      // Auto-redirect to reports page after 5 seconds
      const countdownInterval = setInterval(() => {
        setRedirectCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            stopSpeaking();
            navigate('/candidate/reports');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error('Error completing session:', error);
      alert('Failed to complete session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEndSession = () => {
    if (window.confirm('Are you sure you want to end this training session? Your progress will be saved.')) {
      handleCompleteSession();
    }
  };

  // ─── Computed ───
  const currentQuestion = questions[currentQuestionIndex];
  const progress = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;
  const isLastQuestion = currentQuestionIndex >= questions.length - 1;

  // ─── Phase badge helper ───
  const getPhaseBadge = () => {
    switch (answerPhase) {
      case 'thinking': return { color: 'bg-amber-500', text: 'Thinking...', icon: 'psychology' };
      case 'listening': return { color: 'bg-green-500', text: `Recording (${listeningRemaining}s)`, icon: 'mic' };
      case 'evaluating': return { color: 'bg-purple-500', text: 'Evaluating...', icon: 'auto_awesome' };
      case 'feedback': return { color: 'bg-teal-500', text: 'Feedback', icon: 'grade' };
      default: return isSpeaking ? { color: 'bg-blue-500', text: 'AI Speaking...', icon: 'volume_up' } : { color: 'bg-gray-400', text: 'Waiting', icon: 'hourglass_empty' };
    }
  };

  // ═════════════════════ LOADING ═════════════════════
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ background: colors.bg }}>
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full border-4 border-gray-200"></div>
            <div className="absolute inset-0 rounded-full border-4 border-t-teal-500 animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Icon name="psychology" size={28} style={{ color: colors.primary }} />
            </div>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {sessionComplete ? 'Wrapping Up...' : 'Preparing Your Training'}
          </h2>
          <p className="text-gray-500 text-sm">
            {effectiveTrainingData?.jobTitle || trainingData?.jobTitle || 'AI Training'}
          </p>
        </div>
      </div>
    );
  }

  // ═════════════════════ COMPLETE ═════════════════════
  if (sessionComplete && sessionSummary) {
    const score = Math.round(sessionSummary.overall_score);
    const scoreColor = score >= 80 ? colors.success : score >= 60 ? colors.warning : colors.danger;

    return (
      <div className="min-h-screen" style={{ background: `linear-gradient(135deg, ${colors.bg} 0%, #e0f2f1 100%)` }}>
        <div className="max-w-3xl mx-auto px-6 py-12">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: scoreColor }}>
              <Icon name="emoji_events" size={40} className="text-white" />
            </div>
            <h1 className="text-3xl font-extrabold text-gray-900 mb-2">Training Complete!</h1>
            <p className="text-gray-500 text-lg">Here's how you performed</p>
          </div>

          {/* Score Card */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
            <div className="flex items-center justify-center gap-8 mb-8">
              <div className="text-center">
                <div className="text-6xl font-black" style={{ color: scoreColor }}>{score}%</div>
                <div className="text-sm font-semibold text-gray-400 uppercase tracking-wider mt-1">Overall Score</div>
              </div>
              <div className="w-px h-16 bg-gray-200"></div>
              <div className="text-center">
                <div className="text-5xl font-black text-gray-800">{sessionSummary.overall_grade}</div>
                <div className="text-sm font-semibold text-gray-400 uppercase tracking-wider mt-1">Grade</div>
              </div>
              <div className="w-px h-16 bg-gray-200"></div>
              <div className="text-center">
                <div className="text-5xl font-black text-gray-800">{sessionSummary.answered_questions}/{sessionSummary.total_questions}</div>
                <div className="text-sm font-semibold text-gray-400 uppercase tracking-wider mt-1">Answered</div>
              </div>
            </div>

            {/* Feedback message */}
            <div className="text-center p-4 rounded-xl mb-6" style={{ background: `${scoreColor}15` }}>
              <p className="font-semibold" style={{ color: scoreColor }}>
                {score >= 80 ? 'Excellent performance! You\'re interview-ready.' :
                 score >= 70 ? 'Good job! A bit more practice will make you interview-ready.' :
                 score >= 60 ? 'Keep practicing! Focus on the areas for improvement.' :
                 'More practice needed. Review the feedback and try again.'}
              </p>
            </div>

            {/* Report notice */}
            <div className="flex items-start gap-3 p-4 bg-teal-50 rounded-xl border border-teal-100">
              <Icon name="email" size={22} style={{ color: colors.primary }} className="mt-0.5" />
              <div>
                <p className="font-semibold text-gray-800 text-sm">Detailed Report Sent</p>
                <p className="text-gray-500 text-sm mt-1">
                  A comprehensive PDF report has been sent to <span className="font-semibold" style={{ color: colors.primary }}>{sessionSummary.candidate_email}</span>
                </p>
              </div>
            </div>
          </div>

          {/* Auto-redirect notice */}
          {redirectCountdown > 0 && (
            <div className="text-center mb-4">
              <p className="text-sm text-gray-500">
                Redirecting to your reports in <span className="font-bold text-gray-800">{redirectCountdown}s</span>...
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" onClick={() => { stopSpeaking(); navigate('/candidate/reports'); }} className="flex items-center gap-2">
              <Icon name="assessment" size={20} /> View Reports
            </Button>
            <Button size="lg" onClick={() => { stopSpeaking(); navigate('/ai-trainer'); }} className="flex items-center gap-2">
              <Icon name="refresh" size={20} /> New Session
            </Button>
            <Button variant="secondary" size="lg" onClick={() => { stopSpeaking(); navigate('/'); }} className="flex items-center gap-2">
              <Icon name="home" size={20} /> Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ═════════════════════ INTRO / WELCOME ═════════════════════
  if (showingIntro) {
    return (
      <div className="flex h-screen w-full overflow-hidden" style={{ background: colors.bg }}>
        {/* Warning Banner */}
        {warningBanner.show && (
          <div className="absolute top-0 left-0 right-0 z-50 bg-red-500 text-white px-4 py-3 text-center font-semibold text-sm">
            <Icon name="warning" size={16} className="inline mr-2" />{warningBanner.message}
          </div>
        )}

        {/* ─── Left: Main Content ─── */}
        <div className="flex-1 flex flex-col">
          {/* Top Bar */}
          <div className="h-14 px-6 flex items-center justify-between bg-white border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: colors.primary }}>
                <Icon name="psychology" size={18} className="text-white" />
              </div>
              <h1 className="text-lg font-bold text-gray-900">AI Interview Training</h1>
            </div>
            <div className="flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full" style={{ background: colors.primaryLight, color: colors.primaryText }}>
              <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: colors.success }}></div>
              AI Ready
            </div>
          </div>

          {/* Welcome Content */}
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="max-w-lg text-center">
              <div className="w-24 h-24 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ background: colors.primaryLight }}>
                <Icon name="school" size={48} style={{ color: colors.primary }} />
              </div>
              <h2 className="text-3xl font-extrabold text-gray-900 mb-3">Ready to Begin?</h2>
              <p className="text-gray-500 text-base mb-8 leading-relaxed">
                Your training consists of <span className="font-bold text-gray-700">{questions.length} questions</span> designed to sharpen your interview skills.
                You'll receive <span className="font-bold text-gray-700">real-time AI feedback</span> after each answer.
              </p>

              {/* Speaking indicator */}
              {isSpeaking && (
                <div className="flex items-center justify-center gap-2 mb-6 p-3 rounded-xl bg-blue-50 border border-blue-100">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="w-1 bg-blue-500 rounded-full animate-pulse" style={{ height: `${10 + Math.random() * 14}px`, animationDelay: `${i * 100}ms` }} />
                    ))}
                  </div>
                  <span className="text-sm font-medium text-blue-600 ml-2">AI is speaking introduction...</span>
                </div>
              )}

              {/* Start Training Button */}
              <button
                onClick={startFirstQuestion}
                disabled={isSpeaking}
                className="w-full py-4 px-8 rounded-xl text-white font-bold text-lg transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0"
                style={{ background: isSpeaking ? '#94a3b8' : colors.primary }}
              >
                <Icon name="play_arrow" size={24} />
                {isSpeaking ? 'Listening to Introduction...' : 'Start Training'}
              </button>

              <p className="text-xs text-gray-400 mt-4">Make sure your camera and microphone are working</p>
            </div>
          </div>
        </div>

        {/* ─── Right: Camera Panel ─── */}
        <div className="w-[350px] bg-white border-l border-gray-200 flex flex-col">
          {/* Camera Feed */}
          <div className="relative bg-gray-900 overflow-hidden" style={{ height: '280px' }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ transform: 'scaleX(-1)' }}
            />
            {/* Face detection badge */}
            <div className="absolute top-3 right-3 z-10">
              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold backdrop-blur-sm ${
                faceDetectionStatus === 'ok' ? 'bg-green-500/90 text-white' : 'bg-amber-500/90 text-white'
              }`}>
                <Icon name={faceDetectionStatus === 'ok' ? 'face' : 'face_retouching_off'} size={14} />
                {faceDetectionStatus === 'ok' ? 'Detected' : 'Checking'}
              </div>
            </div>
            {/* No camera overlay */}
            {!cameraStreamRef.current && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900">
                <Icon name="videocam_off" size={40} className="text-gray-600 mb-2" />
                <p className="text-gray-500 text-xs">Starting camera...</p>
              </div>
            )}
          </div>

          {/* Monitoring */}
          <div className="p-4 border-b border-gray-100">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Monitoring</h3>
            <div className="grid grid-cols-3 gap-2">
              {[
                { icon: 'face', label: 'Face', status: faceDetectionStatus === 'ok' ? 'OK' : 'Checking', ok: faceDetectionStatus === 'ok' },
                { icon: 'visibility', label: 'Eyes', status: 'Focused', ok: true },
                { icon: 'mic', label: 'Mic', status: cameraStreamRef.current ? 'Ready' : 'Off', ok: !!cameraStreamRef.current },
              ].map(item => (
                <div key={item.label} className="text-center p-2.5 bg-gray-50 rounded-lg">
                  <Icon name={item.icon} size={20} className={`mx-auto mb-1 ${item.ok ? 'text-green-500' : 'text-amber-500'}`} />
                  <div className="text-[10px] font-semibold text-gray-500">{item.label}</div>
                  <div className={`text-[10px] font-bold ${item.ok ? 'text-green-600' : 'text-amber-600'}`}>{item.status}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Session Info */}
          <div className="p-4 border-b border-gray-100">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Session Info</h3>
            <div className="space-y-2.5">
              {[
                { label: 'Role', value: effectiveTrainingData?.jobTitle || 'General' },
                { label: 'Type', value: effectiveTrainingData?.sessionType || 'AI Training' },
                { label: 'Questions', value: `${questions.length} total` },
                { label: 'Difficulty', value: effectiveTrainingData?.difficultyLevel || 'Medium' },
              ].map(item => (
                <div key={item.label} className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">{item.label}</span>
                  <span className="text-xs font-bold text-gray-700">{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Status */}
          <div className="p-4 mt-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: colors.success }}></div>
                <span className="text-xs font-semibold text-gray-600">Ready</span>
              </div>
              <span className="text-xs font-bold text-gray-400">0/{questions.length}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ═════════════════════ MAIN TRAINING INTERFACE ═════════════════════
  const phaseBadge = getPhaseBadge();

  return (
    <div className="flex h-screen w-full overflow-hidden" style={{ background: colors.bg }}>
      {/* Warning Banner */}
      {warningBanner.show && (
        <div className="absolute top-0 left-0 right-0 z-50 bg-red-500 text-white px-4 py-3 text-center font-semibold text-sm animate-pulse">
          <Icon name="warning" size={16} className="inline mr-2" />{warningBanner.message}
        </div>
      )}

      {/* ─── Left: Question & Controls ─── */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <div className="h-14 px-6 flex items-center justify-between bg-white border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: colors.primary }}>
              <Icon name="psychology" size={18} className="text-white" />
            </div>
            <h1 className="text-lg font-bold text-gray-900">AI Interview Training</h1>
          </div>
          <div className="flex items-center gap-4">
            {/* Phase Badge */}
            <div className={`flex items-center gap-2 text-xs font-semibold text-white px-3 py-1.5 rounded-full ${phaseBadge.color}`}>
              <Icon name={phaseBadge.icon} size={14} />
              {phaseBadge.text}
            </div>
            {/* Timer */}
            {questionTimer > 0 && (
              <div className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full ${
                questionTimer <= 10 ? 'bg-red-100 text-red-600' : questionTimer <= 30 ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-600'
              }`}>
                <Icon name="timer" size={14} />
                {Math.floor(questionTimer / 60)}:{String(questionTimer % 60).padStart(2, '0')}
              </div>
            )}
            {/* Progress */}
            <span className="text-xs font-bold text-gray-500">
              Q{currentQuestionIndex + 1}/{questions.length}
            </span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-gray-200">
          <div className="h-full transition-all duration-500" style={{ width: `${progress}%`, background: colors.primary }}></div>
        </div>

        {/* Question Area */}
        <div className="flex-1 flex flex-col p-6 overflow-auto">
          {/* Question Card */}
          {currentQuestion && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: colors.primaryLight }}>
                  <span className="text-sm font-black" style={{ color: colors.primary }}>Q{currentQuestion.question_number}</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: colors.primaryLight, color: colors.primaryText }}>
                      {currentQuestion.category || 'technical'}
                    </span>
                  </div>
                  <h2 className="text-lg font-bold text-gray-900 leading-snug">
                    {currentQuestion.question_text}
                  </h2>
                  {currentQuestion.tips && (
                    <p className="text-xs text-gray-400 mt-3 leading-relaxed">
                      <span className="font-semibold text-gray-500">Tip:</span> {currentQuestion.tips}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Status Area */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex-1 min-h-[200px] flex flex-col">
            {/* AI Speaking */}
            {isSpeaking && answerPhase !== 'feedback' && (
              <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100 mb-4">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-1 bg-blue-500 rounded-full animate-pulse" style={{ height: `${10 + Math.random() * 16}px`, animationDelay: `${i * 120}ms` }} />
                  ))}
                </div>
                <span className="text-sm font-semibold text-blue-600">AI is reading the question...</span>
              </div>
            )}

            {/* Thinking */}
            {answerPhase === 'thinking' && (
              <div className="flex-1 flex flex-col items-center justify-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: '#fef3c7' }}>
                  <Icon name="psychology" size={32} className="text-amber-500" />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-1">Take a moment to think...</h3>
                <p className="text-sm text-gray-500">Organize your thoughts before answering</p>
              </div>
            )}

            {/* Listening — Recording */}
            {answerPhase === 'listening' && (
              <div className="flex-1 flex flex-col items-center justify-center">
                <div className="relative mb-5">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center bg-green-100 animate-pulse">
                    <Icon name="mic" size={36} className="text-green-600" />
                  </div>
                  <div className="absolute -inset-2 rounded-full border-2 border-green-400 animate-ping opacity-30"></div>
                </div>
                <div className="flex items-center gap-1.5 mb-3">
                  {[...Array(9)].map((_, i) => (
                    <div
                      key={i}
                      className="w-1.5 bg-green-500 rounded-full"
                      style={{
                        height: `${8 + Math.sin(Date.now() / 200 + i) * 16}px`,
                        animation: 'pulse 0.6s ease-in-out infinite',
                        animationDelay: `${i * 70}ms`,
                      }}
                    />
                  ))}
                </div>
                <h3 className="text-lg font-bold text-green-700 mb-1">Listening to your answer...</h3>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Icon name="timer" size={16} className={questionTimer <= 10 ? 'text-red-500' : 'text-green-600'} />
                    <span className={`font-bold ${questionTimer <= 10 ? 'text-red-600 animate-pulse' : 'text-green-600'}`}>
                      {Math.floor(questionTimer / 60)}:{String(questionTimer % 60).padStart(2, '0')}
                    </span>
                  </div>
                </div>

                {/* Submit button is in the bottom action bar */}
              </div>
            )}

            {/* Evaluating */}
            {answerPhase === 'evaluating' && (
              <div className="flex-1 flex flex-col items-center justify-center">
                <div className="relative w-16 h-16 mb-4">
                  <div className="absolute inset-0 rounded-full border-4 border-purple-100"></div>
                  <div className="absolute inset-0 rounded-full border-4 border-t-purple-500 animate-spin"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Icon name="auto_awesome" size={24} className="text-purple-500" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-purple-700 mb-1">Evaluating with AI...</h3>
                <p className="text-sm text-gray-500">Analyzing your response</p>
              </div>
            )}

            {/* Feedback */}
            {answerPhase === 'feedback' && lastEvaluation && (
              <div className="flex-1 flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Evaluation Result</h3>
                  {isSpeaking && (
                    <div className="flex items-center gap-1.5 text-xs text-blue-500 font-semibold">
                      <Icon name="volume_up" size={14} className="animate-pulse" /> Speaking feedback
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-6 p-5 rounded-xl mb-4" style={{ background: `${lastEvaluation.score >= 70 ? colors.success : lastEvaluation.score >= 50 ? colors.warning : colors.danger}12` }}>
                  <div className="text-center">
                    <div className="text-4xl font-black" style={{ color: lastEvaluation.score >= 70 ? colors.success : lastEvaluation.score >= 50 ? colors.warning : colors.danger }}>
                      {lastEvaluation.score}%
                    </div>
                    <div className="text-xs font-bold text-gray-400 mt-0.5">Score</div>
                  </div>
                  <div className="w-px h-12 bg-gray-200"></div>
                  <div className="text-center">
                    <div className="text-3xl font-black text-gray-700">{lastEvaluation.grade}</div>
                    <div className="text-xs font-bold text-gray-400 mt-0.5">Grade</div>
                  </div>
                </div>
                {lastEvaluation.feedback && (
                  <p className="text-sm text-gray-600 leading-relaxed">{typeof lastEvaluation.feedback === 'string' ? lastEvaluation.feedback : ''}</p>
                )}
              </div>
            )}

            {/* Idle — waiting */}
            {answerPhase === 'idle' && !isSpeaking && (
              <div className="flex-1 flex flex-col items-center justify-center">
                <Icon name="headphones" size={40} className="text-gray-300 mb-3" />
                <p className="text-sm text-gray-400">Waiting for AI to speak the question...</p>
              </div>
            )}
          </div>
        </div>

        {/* ─── Bottom Action Bar ─── */}
        <div className="h-16 px-6 flex items-center justify-center gap-3 bg-white border-t border-gray-200">
          <button
            onClick={skipQuestion}
            disabled={isSpeaking || answerPhase === 'evaluating'}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: '#f1f5f9', color: '#64748b' }}
          >
            <Icon name="skip_next" size={18} />
            Skip
          </button>

          {(answerPhase === 'listening') && (
            <button
              onClick={submitAnswer}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white transition-all duration-200 shadow-md hover:shadow-lg"
              style={{ background: colors.primary }}
            >
              <Icon name="check_circle" size={18} />
              Submit Answer
            </button>
          )}

          {answerPhase === 'feedback' && (
            <button
              onClick={moveToNextQuestion}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white transition-all duration-200 shadow-md hover:shadow-lg"
              style={{ background: colors.primary }}
            >
              <Icon name="arrow_forward" size={18} />
              {isLastQuestion ? 'Complete Session' : 'Next Question'}
            </button>
          )}

          <button
            onClick={handleEndSession}
            disabled={answerPhase === 'evaluating'}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-red-500 bg-red-50 hover:bg-red-100 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed ml-auto"
          >
            <Icon name="stop_circle" size={18} />
            End Session
          </button>
        </div>
      </div>

      {/* ─── Right: Camera Panel ─── */}
      <div className="w-[350px] bg-white border-l border-gray-200 flex flex-col">
        {/* Camera Feed */}
        <div className="relative bg-gray-900 overflow-hidden" style={{ height: '280px' }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
            style={{ transform: 'scaleX(-1)' }}
          />
          {/* Recording badge */}
          {(answerPhase === 'listening') && (
            <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/90 text-white text-xs font-semibold backdrop-blur-sm">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              REC
            </div>
          )}
          {/* Face badge */}
          <div className="absolute top-3 right-3 z-10">
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold backdrop-blur-sm ${
              faceDetectionStatus === 'ok' ? 'bg-green-500/90 text-white' : 'bg-amber-500/90 text-white'
            }`}>
              <Icon name={faceDetectionStatus === 'ok' ? 'face' : 'face_retouching_off'} size={14} />
              {faceDetectionStatus === 'ok' ? 'Detected' : 'Checking'}
            </div>
          </div>
          {/* Proctoring notice */}
          <div className="absolute bottom-0 left-0 right-0 p-2 bg-black/40 backdrop-blur-sm">
            <p className="text-[10px] text-white/70 text-center">AI Proctoring Active</p>
          </div>
        </div>

        {/* Monitoring */}
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Monitoring</h3>
          <div className="grid grid-cols-3 gap-2">
            {[
              { icon: 'face', label: 'Face', status: faceDetectionStatus === 'ok' ? 'OK' : 'Checking', ok: faceDetectionStatus === 'ok' },
              { icon: 'visibility', label: 'Eyes', status: 'Focused', ok: true },
              { icon: isListening ? 'mic' : 'mic_off', label: 'Mic', status: isListening ? 'Active' : 'Standby', ok: isListening },
            ].map(item => (
              <div key={item.label} className="text-center p-2.5 bg-gray-50 rounded-lg">
                <Icon name={item.icon} size={20} className={`mx-auto mb-1 ${item.ok ? 'text-green-500' : 'text-amber-500'}`} />
                <div className="text-[10px] font-semibold text-gray-500">{item.label}</div>
                <div className={`text-[10px] font-bold ${item.ok ? 'text-green-600' : 'text-amber-600'}`}>{item.status}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Session Info */}
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Session</h3>
          <div className="space-y-2.5">
            {[
              { label: 'Role', value: effectiveTrainingData?.jobTitle || 'General' },
              { label: 'Progress', value: `${currentQuestionIndex + 1} of ${questions.length}` },
              { label: 'Category', value: currentQuestion?.category || '-' },
            ].map(item => (
              <div key={item.label} className="flex justify-between items-center">
                <span className="text-xs text-gray-400">{item.label}</span>
                <span className="text-xs font-bold text-gray-700">{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Progress */}
        <div className="p-4 mt-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Progress</span>
            <span className="text-xs font-bold" style={{ color: colors.primary }}>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div className="h-2 rounded-full transition-all duration-500" style={{ width: `${progress}%`, background: colors.primary }}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AITrainerSessionPage;
